import { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { TextField } from '../index';

import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import { decomposeHold, holdCellNum, recomposeHold } from './holdTime';
import { steppedWeight } from './weightStepper';
import { useDockedLoadBar } from './LoggerDock';
import { useFieldFocusReporter } from './useFieldFocus';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';
import { SetRowStepperBar } from './SetRowStepperBar';
import { STEPS_KG, STEPS_LB } from './weightStepConstants';
import { unitWord } from '../weightUnitLabels';

// Timed (isometric) inputs for the timeline FocusPanel — plank, wall-sit,
// bird-dog hold, the physiotherapy isometrics. Same layout as the strength
// fields (FocusPanelInputs): labelled bare-number cells, no inline unit
// suffix crowding the digit (Antenore 2026-06-09: "usa lo stesso layout dei
// pesi"). Columns: h · min · s · extra-load. Duration is one DraftSet.
// duration (seconds); h/min/s derive + recompose via holdTime. The
// start-timer affordance is no longer a row here: it moved into the effort
// meter's slot below (MX-5 slice 2), so this block is only the fields. The
// added-load cell stays editable always (the load is a setup property, entered
// before or after the hold).
export function FocusPanelTimed({
  set,
  onChange,
  theme,
}: {
  set: DraftSet;
  onChange: (patch: Partial<Pick<DraftSet, 'duration' | 'weight'>>) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  // Added-load gets the SAME +X kg stepper as the strength weight field
  // (Antenore 2026-06-09: "l'extra non ha il selettore di peso come gli
  // esercizi standard").
  // Serve SOLO fuori dal dock: vedi il commento su `inFlow` più sotto.
  const [extraFocused, setExtraFocused] = useState(false);
  const [stepSign, setStepSign] = useState<1 | -1>(1);
  const applyDelta = (delta: number) =>
    onChange({ weight: steppedWeight(set.weight, delta) });

  const { h, m, s } = decomposeHold(set.duration);
  const commit = (hh: number, mm: number, ss: number) =>
    onChange({ duration: recomposeHold(hh, mm, ss) });

  // Tap a populated cell → select its digits so you type over them (the
  // focus reveal of the hold-start button re-renders the row and clears RN's
  // native selectTextOnFocus on Android — see useSelectAllOnFocus).
  const hSel = useSelectAllOnFocus(h > 0 ? String(h) : '');
  const mSel = useSelectAllOnFocus(m > 0 ? String(m) : '');
  const sSel = useSelectAllOnFocus(s > 0 ? String(s) : '');
  const extraSel = useSelectAllOnFocus(set.weight);
  // Same signal the reps/weight cells raise: while one of these holds the
  // keyboard the rest-end resume waits its turn (TM-18a). One id per cell —
  // they can hand focus to each other, and a shared id would read as "let go"
  // the moment the second one took over.
  const fieldFocus = useFieldFocusReporter();

  const timeField = (
    label: string,
    value: number,
    onNum: (n: number) => void,
    a11y: string,
    sel: ReturnType<typeof useSelectAllOnFocus>,
    fieldId: string,
  ) => (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextField
        theme={theme}
        numeric
        ref={sel.ref}
        keyboardType="number-pad"
        inputMode="numeric"
        accessibilityLabel={a11y}
        placeholder="0"
        value={value > 0 ? String(value) : ''}
        onChangeText={(txt) => onNum(holdCellNum(txt))}
        onFocus={() => {
          sel.onFocus();
          fieldFocus.onFocus(fieldId);
        }}
        onBlur={() => fieldFocus.onBlur(fieldId)}
        selectTextOnFocus
        maxLength={2}
      />
    </View>
  );

  // ⭐ LG-94 — docked, for the same reason as the weight cell in
  // FocusPanelInputs: in the flow this bar appeared under the keyboard that
  // had just opened to type the number it adjusts.
  //
  // ⭐⭐ LG-95 — e PERMANENTE, non più rivelata dal fuoco. Qui la condizione
  // sparisce del tutto invece di restringersi: l'isometrico HA sempre un asse
  // di carico, ed è già scritto in cima a questo file che "the added-load cell
  // stays editable always". Legarla al fuoco faceva saltare il rest timer, che
  // nel dock sta sopra, e la lasciava agganciata al fondo a tastiera chiusa
  // quando il blur non veniva consegnato.
  const loadBar = (
    <SetRowStepperBar
      unit={set.unit}
      stepSign={stepSign}
      setStepSign={setStepSign}
      stepMagnitudes={set.unit === 'lb' ? STEPS_LB : STEPS_KG}
      applyDelta={applyDelta}
      theme={theme}
    />
  );
  // ⛔ IL FUORI-DOCK RESTA COM'ERA. `useDockedLoadBar` torna `inFlow` non-null
  // solo dove NON c'è un dock: è il program editor, che monta questo stesso
  // pannello nel mazzo di settimane sfogliabile, senza rest timer sopra e
  // senza tastiera che copra la barra. La decisione del 14/09 parla del DOCK e
  // dei suoi due difetti; renderla permanente anche là sarebbe una superficie
  // in più che lui non ha chiesto.
  const { inFlow } = useDockedLoadBar(loadBar, theme);

  return (
    <View style={styles.block}>
      <View style={styles.row}>
        {timeField(
          t('mobile.logger.holdHeaderHours', { defaultValue: 'h' }),
          h,
          (n) => commit(n, m, s),
          t('mobile.logger.holdHours', { defaultValue: 'Hours' }),
          hSel,
          'hold.h',
        )}
        {timeField(
          t('mobile.logger.holdHeaderMinutes', { defaultValue: 'min' }),
          m,
          (n) => commit(h, n, s),
          t('mobile.logger.holdMinutes', { defaultValue: 'Minutes' }),
          mSel,
          'hold.m',
        )}
        {timeField(
          t('mobile.logger.holdHeaderSeconds', { defaultValue: 's' }),
          s,
          (n) => commit(h, m, n),
          t('mobile.logger.holdSeconds', { defaultValue: 'Seconds' }),
          sSel,
          'hold.s',
        )}
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>
            {`${t('mobile.logger.holdExtraWeight', { defaultValue: 'Extra' })} (${unitWord(t, set.unit)})`}
          </Text>
          <TextField
            theme={theme}
            numeric
            ref={extraSel.ref}
            inputMode="decimal"
            placeholder="—"
            value={set.weight}
            onChangeText={(weight) => onChange({ weight })}
            onFocus={() => {
              setExtraFocused(true);
              extraSel.onFocus();
              fieldFocus.onFocus('hold.extra');
            }}
            onBlur={() => {
              setExtraFocused(false);
              fieldFocus.onBlur('hold.extra');
            }}
            selectTextOnFocus
            maxLength={6}
          />
        </View>
      </View>
      {extraFocused ? inFlow : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: { flex: 1, gap: s.s2 },
    row: { flexDirection: 'row', alignItems: 'flex-end', gap: s.s2 },
    // Mirrors FocusPanelInputs' field/fieldLabel/input so a timed set reads
    // identically to a strength set in the timeline panel.
    field: { flex: 1, gap: 4 },
    fieldLabel: { color: p.ink3, fontSize: ty.sizes.xs, fontWeight: '600' },
  });
}
